import { DiscoverBuilding } from '../../_types/quickpass-discover'

const formatCurrencyShort = (value: number): string => {
  if (value <= 0) return ''
  if (value >= 1000) {
    const k = value / 1000
    return `$${k.toFixed(k % 1 === 0 ? 0 : 1)}k`
  }
  return `$${value.toString()}`
}

const formatCurrencyFull = (value: number): string =>
  `$${value.toLocaleString('en-CA')}`

/**
 * Formats the cost range for a building card. Returns a single price when
 * min and max collapse to the same value, a range otherwise, or a "price
 * on request" placeholder when neither bound is set.
 *
 * @param {DiscoverBuilding} building
 * @returns {string}
 */
const formatPriceRange = (building: DiscoverBuilding): string => {
  if (building.costMin <= 0 && building.costMax <= 0) {
    return 'Price on request'
  }
  if (building.costMin > 0 && building.costMax > 0) {
    if (building.costMin === building.costMax) {
      return `${formatCurrencyFull(building.costMin)} / month`
    }
    return `${formatCurrencyFull(building.costMin)} – ${formatCurrencyFull(building.costMax)} / month`
  }
  const value = building.costMin > 0 ? building.costMin : building.costMax
  return `${formatCurrencyFull(value)} / month`
}

/**
 * Compact price for use on map markers — drops the trailing zeros and
 * collapses long ranges into "$1.5k–$3k".
 *
 * @param {DiscoverBuilding} building
 * @returns {string}
 */
const formatPriceRangeShort = (building: DiscoverBuilding): string => {
  if (building.costMin <= 0 && building.costMax <= 0) return '$?'
  if (building.costMin > 0 && building.costMax > 0) {
    if (building.costMin === building.costMax) {
      return formatCurrencyShort(building.costMin)
    }
    return `${formatCurrencyShort(building.costMin)}–${formatCurrencyShort(building.costMax)}`
  }
  return formatCurrencyShort(
    building.costMin > 0 ? building.costMin : building.costMax,
  )
}

const formatBedLabel = (count: number): string => {
  if (count === 0) return 'Studio'
  return `${count.toString()} bed${count === 1 ? '' : 's'}`
}

/**
 * Joins the building's bedroom variety into a compact line — "Studio, 1
 * bed, 2 beds" — and caps at three entries to keep cards readable.
 *
 * @param {DiscoverBuilding} building
 * @returns {string}
 */
const formatBedSummary = (building: DiscoverBuilding): string => {
  if (building.bedroomsList.length === 0) return ''
  if (building.bedroomsList.length === 1) {
    return formatBedLabel(building.bedroomsList[0])
  }

  const sorted = [...building.bedroomsList].sort((left, right) => left - right)
  const min = sorted[0]
  const max = sorted[sorted.length - 1]

  if (min === max) return formatBedLabel(min)
  return `${formatBedLabel(min)} – ${formatBedLabel(max)}`
}

const formatSquareFootageRange = (building: DiscoverBuilding): string => {
  if (building.squareFootageMin <= 0 && building.squareFootageMax <= 0)
    return ''
  if (building.squareFootageMin > 0 && building.squareFootageMax > 0) {
    if (building.squareFootageMin === building.squareFootageMax) {
      return `${building.squareFootageMin.toLocaleString('en-CA')} ft²`
    }
    return `${building.squareFootageMin.toLocaleString('en-CA')}–${building.squareFootageMax.toLocaleString('en-CA')} ft²`
  }
  const value =
    building.squareFootageMin > 0
      ? building.squareFootageMin
      : building.squareFootageMax
  return `${value.toLocaleString('en-CA')} ft²`
}

const STATUS_LABELS: Record<
  'available' | 'available-soon' | 'occupied' | 'unavailable',
  string
> = {
  available: 'Available',
  'available-soon': 'Available soon',
  occupied: 'Currently occupied',
  unavailable: 'Off-market',
}

const formatStatusLabel = (
  availability: 'available' | 'available-soon' | 'occupied' | 'unavailable',
): string => {
  return STATUS_LABELS[availability] ?? 'Unavailable'
}

/**
 * The card's main descriptor — "3 matching 2-bed listings", or
 * "12 matching listings" when the bed-filter wasn't constraining.
 *
 * @param {DiscoverBuilding} building
 * @returns {string}
 */
const formatMatchSummary = (building: DiscoverBuilding): string => {
  const count = building.matchingListingsCount
  if (count <= 1 && building.bedroomsList.length <= 1) {
    if (building.bedroomsList.length === 1) {
      return `1 ${formatBedLabel(building.bedroomsList[0])} listing`
    }
    return '1 listing'
  }
  if (count > 1 && building.bedroomsList.length === 1) {
    return `${count.toString()} ${formatBedLabel(building.bedroomsList[0])} listings`
  }
  return `${count.toString()} matching listings`
}

export {
  formatPriceRange,
  formatPriceRangeShort,
  formatBedSummary,
  formatSquareFootageRange,
  formatStatusLabel,
  formatMatchSummary,
}
